/**
 * Strategy Info — content for the collapsible ⓘ panel inside each strategy card.
 *
 * HOW TO UPDATE: when a strategy evolves, edit the relevant entry here.
 * Use the prompt in PROJECT_CONTEXT.md to extract updated content from your
 * strategy development chat, then paste it into the appropriate object below.
 *
 * Shape per strategy:
 *   principle          — 2–3 sentences, the core thesis in plain English
 *   schedule           — when the scanner runs automatically (shown in the info panel)
 *   requiredFilters    — hard pass/fail conditions (ALL must be true to qualify)
 *   confidenceCriteria — scoring lines starting with "+1  " (two spaces after point)
 *   actionableThreshold — minimum stars / what that means
 *   researchNotes      — 4–8 bullets: variants tested + why current config was chosen
 */

export const STRATEGY_INFO = {

  // ── Strategy 1 — KAMA Pullback + EMA Trigger ────────────────────────────────
  kama_pullback: {
    schedule:
      "Runs automatically every trading day at 16:05 IST (after market close) " +
      "via the Railway backend scheduler. Requires Kite Connect to be logged in. " +
      "No manual action needed — signals are ready by ~16:10 IST each evening.",

    principle:
      "Identifies stocks where institutional momentum has shifted via KAMA " +
      "alignment, then waits for a pullback to the KAMA line followed by an " +
      "EMA crossover re-trigger. Designed for buying Far-OTM CE/PE options in " +
      "week 1–2 of a monthly expiry (~0.25 delta, 21 DTE) when the R/R is " +
      "most favourable and time decay has not yet become punishing.",

    requiredFilters: [
      "KAMA(daily) > KAMA(weekly approx) — institutional bias aligned upward for CE / downward for PE",
      "Stock's daily LOW touched below KAMA(daily) in the last 5 candles — confirms pullback to structure",
      "EMA(3) crossed above EMA(9) within the last 1–3 candles — momentum re-trigger after the pullback",
    ],

    confidenceCriteria: [
      "+1  KAMA daily crossed above KAMA-weekly-approx within last 20 days (fresh alignment)",
      "+1  KAMA cross is clean — no re-crossover in the prior 20 days before the cross",
      "+1  ATR(14)/Close > 1.5% — stock moves enough to make options viable",
      "+1  20-day high–low range > 8% — not stuck in a tight sideways zone",
      "+1  EMA3/EMA9 crossovers ≤ 2 in last 10 days — clean signal, not whipsawing",
      "+1  Volume on EMA crossover day > 1.2× 20-day average — institutional participation",
      "+1  RS vs NIFTY positive over 10 days — stock is outperforming the index",
    ],

    actionableThreshold:
      "4★ and above — 4★ is a solid setup with most filters aligned; " +
      "5★ is highest conviction. 2★ and 3★ are monitored but not traded.",

    researchNotes: [
      "Universe: 35 curated F&O stocks selected for liquidity and options viability",
      "KAMA(20, 2, 30) — n=20 for responsiveness, fast=2/slow=30 for wide smoothing range",
      "Weekly KAMA approximated from daily data (no separate weekly candle fetch needed)",
      "Bearish mirror: all logic reversed — KAMA daily < weekly, HIGH touches KAMA, EMA3 crosses below EMA9",
      "Stars 0–1 → 2★, 2–3 → 3★, 4–5 → 4★, 6–7 → 5★ — only 4★+ considered actionable",
      "Options sizing: Far-OTM at ~0.25 delta, 21 DTE, week 1–2 of monthly expiry only",
    ],
  },

  // ── Strategy 4 — Weekly NIFTY AI Debit Spread ───────────────────────────────
  weekly_nifty_spread: {
    schedule:
      "Prediction generated on demand each Wednesday morning before market open. " +
      "Click 'Generate Prediction' in the Strategy 4 card. A two-step AI pipeline " +
      "runs: Sonnet synthesises the macro backdrop from recent news, then Opus " +
      "produces the directional call. Takes ~15–25 seconds on first call; " +
      "result is cached for the rest of the week.",

    principle:
      "An AI-powered weekly directional trade on NIFTY using a two-leg debit spread " +
      "structure. The primary leg is an OTM CE (bullish) or PE (bearish) debit spread, " +
      "~200 pts OTM and 200 pts wide, on the current-week Tuesday expiry at full size. " +
      "A delta-opposite hedge is added on next-week's Tuesday expiry at 2:5 sizing " +
      "(2 lots per 5 primary), exploiting a 2× theta advantage: the hedge has ~13 DTE " +
      "vs ~6 DTE for the primary when entered Wednesday morning.",

    requiredFilters: [
      "IV Percentile < 60% — debit spreads are not viable in expensive IV environments",
      "No Indian Union Budget week — extreme binary event; AI will return neutral with special note",
      "Confidence ≥ 40% — predictions below threshold are displayed but not traded",
      "Entry window: Wednesday morning only (before 10:30 IST) — theta clock starts after open",
    ],

    confidenceCriteria: [
      "+weight  PCR (Put-Call Ratio): <0.7 bullish / 0.7–1.1 neutral / >1.1 bearish",
      "+weight  Max Pain vs Spot: spot below max pain = price pull bullish; above = bearish",
      "+weight  FII Net Futures (daily + 5-day cumulative): institutional directional conviction",
      "+weight  IV Regime (IV percentile + IV-RV spread): cheap IV favours debit entry",
      "+weight  Technicals: 20-EMA position + weekly trend structure (HH/HL vs LH/LL)",
      "+weight  Global Markets (SPY, QQQ, Nikkei, Hang Seng): sustained risk-on/off regime",
      "+weight  Market Breadth (NIFTY 50 advances vs declines): internal market strength",
      "+weight  VIX Level + trend: <14 calm / 14–18 normal / >18 elevated → reduces confidence",
      "Macro events this week automatically reduce confidence: FOMC −20–30 pts, RBI MPC −15–25 pts, US CPI −10–15 pts, US NFP −10–15 pts",
    ],

    actionableThreshold:
      "Confidence ≥ 70% — strong signal alignment with no major event overrides. " +
      "Confidence 40–69% is moderate: reduce size to 50% or skip. " +
      "Below 40%: AI returns neutral regardless of signal lean — do not trade.",

    researchNotes: [
      "Primary leg: CE debit spread (bullish call) or PE debit spread (bearish put) — 200pt wide, ~200pt OTM, current-week Tuesday expiry, full size",
      "Hedge leg: opposite direction, next-week Tuesday expiry, 2:5 sizing — 2 lots per 5 primary lots (1:2.5 ratio)",
      "Hedge theta math: hedge entered at ~13 DTE decays at ~half the rate of primary at ~6 DTE → theta-neutral hedge at 2:5 vs 1:3 sizing",
      "NIFTY weekly expiry is Tuesday; SENSEX Thursday — both liquid enough for spreads; BankNifty moved to monthly-only",
      "Two-step AI: Sonnet synthesises macro backdrop from last 10–14 days HIGH-impact news → Opus makes directional prediction using backdrop + 12 market signals + 16-week history",
      "Historical calibration deliberately dropped — regime mismatch risk on ~100 data points outweighs benefit; live 16-week track record is the right calibration source",
      "Ground truth thresholds: Very Bullish >+1.5% | Mild Bullish +0.5–1.5% | Neutral ±0.5% | mirrored bearish (Monday open → Tuesday expiry close)",
      "Performance scoring: Perfect (deep green) / Correct (light green) / Neutral (grey) / Mild Wrong (amber) / Wrong (red) — bar height = confidence",
    ],
  },

  // ── Strategy 2 — Contrarian SIP ─────────────────────────────────────────────
  contrarian_sip: {
    schedule:
      "Runs automatically every Monday at 15:00 IST via the Railway backend scheduler. " +
      "Uses yfinance data — no Kite login required. Results are ready by ~15:10 IST. " +
      "If Monday is a trading holiday, run manually using the Run Now button.",

    principle:
      "Buy the 2 worst-performing NIFTY 100 stocks each week as a systematic SIP, " +
      "betting on mean reversion over a long holding period. No exits, no stop losses " +
      "— pure accumulation. The contrarian edge is structural: it outperforms a plain " +
      "NIFTY 50 SIP in every one of the 12 tested configurations across 11 years of data.",

    requiredFilters: [
      "Ranked in the bottom 2 of NIFTY 100 by 30-trading-day return (worst performers first; lookback is trading days, not calendar days)",
      "Cap check: the strategy has selected this stock fewer than 5 times in the last 12 months (counted from its own signals, independent of your executions)",
      "₹10,000 invested per selected stock · max 2 stocks per week · no regime or sector guards",
    ],

    confidenceCriteria: [],  // No scoring — selection is purely quantitative ranking

    actionableThreshold:
      "All selected stocks are actionable — no minimum threshold. The 5-trade rolling " +
      "cap per stock per year is the only concentration control.",

    researchNotes: [
      "Iteration 1 — 48 combinations tested (4 lookbacks × 3 thresholds × NIFTY 50 + 100): all 48 beat NIFTY SIP",
      "Iteration 2 — Survivorship bias corrected: Yes Bank drag reduces honest CAGR from ~15% to ~12%",
      "Iteration 3 — Period robustness: strategy outperforms benchmark in both 2015–2020 and 2020–2026",
      "Iteration 5 — Momentum vs Contrarian full grid: contrarian wins CAGR in 12/12 combinations",
      "Iteration 6 — TotalWealth optimisation: deploying every week at 0% threshold maximises absolute wealth",
      "NIFTY 100 chosen over NIFTY 50: ~1.5–2% CAGR advantage across all configs (wider universe wins)",
      "V1 Pure (no filters) beats V2 Filtered — sector guards and regime filters blocked best contrarian entries",
      "30 trading-day lookback at 0% threshold: maximises deployment frequency → highest total wealth over time",
    ],
  },

};
